/**
 * Row geometry of the virtualised reader (UX-MAP-02): an estimated height per paragraph from its length and the measured
 * line height, corrected as rows are rendered and measured (a height cache per paragraph, reset when the column width
 * changes), and the prefix sums that map a scroll offset to a paragraph and back. Pure; no DOM.
 */
import {
	BLOCK_CODE,
	BLOCK_HEADING,
	BLOCK_SCENE_BREAK,
	type ViewModel,
} from '../../shared/viewmodel.ts';

export interface Metrics {
	/** Width of the text column in CSS px. */
	width: number;
	/** Reading font size and line height in CSS px. */
	font: number;
	line: number;
}

/** Fixed pieces of a row (kept in step with reader.css). */
const ROW_PAD = 16;
const H2_EXTRA = 65;
const H3_EXTRA = 46;
const SCENE_H = 41;
/** Average character width of the reading face, in ems. */
const CHAR_EM = 0.52;

export function estimateHeights(vm: ViewModel, m: Metrics): Float32Array {
	const n = vm.paras.count;
	const out = new Float32Array(n);
	const cpl = Math.max(16, Math.floor(m.width / (m.font * CHAR_EM)));
	const { blocks, paras } = vm;
	for (let p = 0; p < n; p++) {
		let h = ROW_PAD;
		const b0 = paras.blockFirst[p] as number;
		const b1 = paras.blockFirst[p + 1] as number;
		if (b1 <= b0) {
			const len = (paras.charEnd[p] as number) - (paras.charStart[p] as number);
			h += Math.max(1, Math.ceil(len / cpl)) * m.line;
		}
		for (let b = b0; b < b1; b++) {
			const kind = blocks.kind[b] as number;
			if (kind === BLOCK_SCENE_BREAK) {
				h += SCENE_H;
			} else if (kind === BLOCK_HEADING) {
				h += (blocks.level[b] as number) <= 2 ? H2_EXTRA : H3_EXTRA;
			} else {
				const len = (blocks.charEnd[b] as number) - (blocks.charStart[b] as number);
				const lines =
					kind === BLOCK_CODE
						? Math.max(1, Math.ceil(len / (cpl * 1.15)))
						: Math.max(1, Math.ceil(len / cpl));
				h += lines * m.line + 4;
			}
		}
		out[p] = h;
	}
	return out;
}

export class Geometry {
	readonly n: number;
	est: Float32Array;
	/** Measured heights; 0 = not measured at the current width. */
	measured: Float32Array;
	/** offsets[p] = top of paragraph p in px from the first row; offsets[n] = total. */
	offsets: Float64Array;
	/** Ratio of measured to estimated height over the measured rows, applied to the unmeasured ones. */
	scale = 1;
	private sumM = 0;
	private sumE = 0;
	width = 0;

	constructor(n: number, est: Float32Array) {
		this.n = n;
		this.est = est;
		this.measured = new Float32Array(n);
		this.offsets = new Float64Array(n + 1);
		this.rebuild();
	}

	/** New estimates (the column width or font changed): forget every measurement, keep the calibration. */
	reset(est: Float32Array, width: number): void {
		this.est = est;
		this.measured.fill(0);
		this.sumM = 0;
		this.sumE = 0;
		this.width = width;
		this.rebuild();
	}

	height(p: number): number {
		const m = this.measured[p] as number;
		return m > 0 ? m : (this.est[p] as number) * this.scale;
	}

	/** Record a measured height. Returns true when it changed the layout. */
	measure(p: number, h: number): boolean {
		const old = this.measured[p] as number;
		if (Math.abs(old - h) < 0.01) return false;
		const e = this.est[p] as number;
		if (old > 0) {
			this.sumM -= old;
			this.sumE -= e;
		}
		this.measured[p] = h;
		this.sumM += h;
		this.sumE += e;
		return true;
	}

	/** Recompute `scale` from the measurements so far. */
	recalibrate(): void {
		this.scale = this.sumE > 0 ? Math.min(3, Math.max(0.4, this.sumM / this.sumE)) : 1;
	}

	rebuild(): void {
		let y = 0;
		const o = this.offsets;
		for (let p = 0; p < this.n; p++) {
			o[p] = y;
			y += this.height(p);
		}
		o[this.n] = y;
	}

	total(): number {
		return this.offsets[this.n] as number;
	}

	top(p: number): number {
		return this.offsets[Math.max(0, Math.min(this.n, p))] as number;
	}

	/** The paragraph containing content offset `y` (clamped to the first and last). */
	at(y: number): number {
		const o = this.offsets;
		let lo = 0;
		let hi = this.n - 1;
		if (hi < 0) return 0;
		while (lo < hi) {
			const mid = (lo + hi + 1) >> 1;
			if ((o[mid] as number) <= y) lo = mid;
			else hi = mid - 1;
		}
		return lo;
	}
}

/**
 * The paragraph window to render around the visible range [v0, v1] (inclusive): `over` px of overscan on each side,
 * at most `maxRows` paragraphs, and at most `maxMarks` marks by an upper bound of two per span touching the window
 * (a cut at every span boundary makes at most 2k + 1 pieces per block). The visible range itself is never trimmed.
 */
export function windowFor(
	vm: ViewModel,
	g: Geometry,
	v0: number,
	v1: number,
	over: number,
	maxRows: number,
	maxMarks: number
): { start: number; end: number } {
	const n = g.n;
	let a = g.at(g.top(v0) - over);
	let b = Math.min(n, g.at(g.top(v1 + 1) + over) + 1);
	const s = vm.spans;
	const cost = (x: number, y: number): number =>
		2 * ((s.paraFirst[y] as number) - (s.paraFirst[x] as number)) +
		2 * ((s.coverStart[y] as number) - (s.coverStart[x] as number)) +
		(y - x);
	// trim the side with more overscan first
	while ((b - a > maxRows || cost(a, b) > maxMarks) && (a < v0 || b > v1 + 1)) {
		if (v0 - a >= b - (v1 + 1) && a < v0) a++;
		else if (b > v1 + 1) b--;
		else a++;
	}
	return { start: a, end: Math.max(b, a + 1) };
}
